import {
  articleCodeFromName,
  stripDirectionSuffix,
  thicknessKey,
} from "./articleCodes";
import { isArticleFreePart } from "./partVisibility";
import { getPianoHingeSegments } from "./pianoHinge";
import { getProfileSegmentLengths } from "./profile";
import {
  getTrayMaterialCodeForStrength,
  isDefaultTrayColor,
} from "./trayMaterialCodes";
import {
  type MaterialInfo,
  MaterialType,
  type PartLike,
  PriceUnit,
  type Vector2Like,
} from "./types";

const PANEL_TYPE = {
  OuterPanel: 0,
  TrayPanel: 1,
  RollerBoard: 2,
  Underlay: 3,
  SlideRail: 4,
} as const;

type PanelTypeName =
  | "OuterPanel"
  | "TrayPanel"
  | "RollerBoard"
  | "Underlay"
  | "SlideRail";

function normalizePanelType(value: unknown): PanelTypeName | undefined {
  if (typeof value === "number") {
    switch (value) {
      case PANEL_TYPE.OuterPanel:
        return "OuterPanel";
      case PANEL_TYPE.TrayPanel:
        return "TrayPanel";
      case PANEL_TYPE.RollerBoard:
        return "RollerBoard";
      case PANEL_TYPE.Underlay:
        return "Underlay";
      case PANEL_TYPE.SlideRail:
        return "SlideRail";
      default:
        return undefined;
    }
  }
  if (typeof value === "string") {
    if (
      value === "OuterPanel" ||
      value === "TrayPanel" ||
      value === "RollerBoard" ||
      value === "Underlay" ||
      value === "SlideRail"
    ) {
      return value;
    }
  }
  return undefined;
}

function getSizeXY(
  size: Vector2Like | [number, number] | undefined,
): { x: number; y: number } | undefined {
  if (!size) return undefined;
  if (Array.isArray(size)) {
    return { x: size[0], y: size[1] };
  }
  if (typeof size === "object" && "x" in size && "y" in size) {
    return { x: size.x, y: size.y };
  }
  return undefined;
}

const ROLLBRETT_NAME = /^rollbrett\b/i;

function isRollbrettLabel(value: unknown): boolean {
  return typeof value === "string" && ROLLBRETT_NAME.test(value.trim());
}

function isCreeperBundlePartSelection(
  partSelection: PartLike["partSelection"],
): boolean {
  if (!partSelection || typeof partSelection !== "object") {
    return false;
  }
  const meshes = (partSelection as { meshes?: Record<string, unknown> }).meshes;
  if (!meshes || typeof meshes !== "object") {
    return false;
  }
  return "rollerA" in meshes && "rollerB" in meshes;
}

function rollbrettBoardDescription(props: PartLike, braked: boolean): string {
  void braked;
  const strength =
    props.strength ?? props.material?.strength ?? props.partSelection?.strength;
  return typeof strength === "number"
    ? `Trennbrett ${strength}mm`
    : "Trennbrett";
}

function getPadCode(style: string | undefined): string | undefined {
  switch (style) {
    case "standard":
      return "STN_PLN_29";
    case "eggbox":
      return "STN_PEN_5023";
    case "slotted":
      // Slotted foam exists as an article only at 15 mm (SST019615, priced per
      // m² through its catalog partSelection; customer export checked
      // 2026-10-29). Other thicknesses stay unpriced until an article exists.
      return undefined;
    case "soft":
      return "STN_PEW_5023";
    default:
      return undefined;
  }
}

/**
 * Whether pads of this style are priced by volume and so exist in any
 * strength. A style without a volumetric article (slotted foam) exists only
 * in the strengths of its catalog articles.
 */
export function paddingStyleHasVolumetricArticle(
  style: string | undefined,
): boolean {
  return getPadCode(style) !== undefined;
}

function getPanelStrength(props: PartLike): number | undefined {
  return (
    props.material?.strength ?? props.partSelection?.strength ?? props.strength
  );
}

function getPanelColor(
  props: PartLike,
): { codesByThickness?: Record<string, string>; name?: string } | undefined {
  const color = props.color;
  if (color == null || typeof color !== "object" || Array.isArray(color)) {
    return undefined;
  }
  return color as { codesByThickness?: Record<string, string>; name?: string };
}

function getPanelMaterialCode(props: PartLike): string | undefined {
  const panelType = normalizePanelType(props.panelType);
  const color = getPanelColor(props);

  const codeForStrength = (strength: number): string | undefined =>
    color?.codesByThickness?.[thicknessKey(strength)];

  switch (panelType) {
    case "SlideRail": {
      const strength = getPanelStrength(props);
      return strength === 18 ? "1004" : "1005";
    }
    case "RollerBoard":
      return props.braked ? "1001b" : "1000b";
    case "OuterPanel": {
      const strength = props.strength ?? getPanelStrength(props);
      return strength != null ? codeForStrength(strength) : undefined;
    }
    default:
      return (
        props.material?.name ??
        (props.strength != null
          ? codeForStrength(props.strength)
          : undefined) ??
        color?.name ??
        props.partSelection?.name
      );
  }
}

/**
 * Parts we intentionally leave unpriced (so `calculatePrice` does not report a
 * false "missing material code"). A split rollbrett produces multiple board
 * panels but is priced once via the `n === 0` panel (1000b/1001b).
 */
export function isUnpricedByDesign(part: PartLike): boolean {
  const props = part.properties ?? part;
  return (
    props.type === "Panel" &&
    normalizePanelType(props.panelType) === "RollerBoard" &&
    props.n !== 0
  );
}

function resolveTrayPanelMaterialCode(
  props: PartLike,
  strength: number,
): string | undefined {
  const color = getPanelColor(props);
  const colorCode = color?.codesByThickness?.[thicknessKey(strength)];

  if (colorCode && colorCode !== "DEFAULT") {
    return colorCode;
  }
  // The selected tray material carries its own article code. Prefer it over the
  // strength lookup table, which maps a thickness to exactly one article and so
  // silently priced every 15mm tray as the same board.
  if (props.material?.name) {
    return props.material.name;
  }
  if (colorCode === "DEFAULT" || isDefaultTrayColor(color)) {
    return getTrayMaterialCodeForStrength(strength) ?? undefined;
  }
  return undefined;
}

function priceAngleSection(props: PartLike): MaterialInfo | null {
  if (!props.partSelection?.name) return null;
  if (typeof props.length !== "number") return null;
  return {
    amount: props.length * 1e-3,
    code: props.partSelection.name,
    description: props.partSelection.description ?? "",
    type: MaterialType.Alu,
    unit: PriceUnit.Meter,
  };
}

function pricePartSelectionPiece(
  props: PartLike,
  toCode: (name: string) => string,
): MaterialInfo | null {
  if (!props.partSelection?.name) return null;
  return {
    amount: 1,
    description: props.partSelection.description ?? "",
    code: toCode(props.partSelection.name),
    type: MaterialType.Other,
    unit: PriceUnit.Piece,
  };
}

/** Pad volume in m³ from its size and strength (mm), as the volumetric foam articles are priced. */
function getPadVolumeM3(props: PartLike): number | undefined {
  const size = getSizeXY(props.size);
  if (!size || typeof props.strength !== "number") return undefined;
  return size.x * size.y * props.strength * 1e-9;
}

/** Style labels as the configurator shows them. */
export const PAD_STYLE_LABELS: Readonly<Record<string, string>> = {
  standard: "Standard",
  soft: "Weich",
  eggbox: "Noppen",
  slotted: "Geschlitzt",
};

/** A parts-list row for a pad that has no article. */
export interface PadWithoutArticleInfo {
  description: string;
  amount: number;
  unit: PriceUnit.CubicMeter;
}

/**
 * Describes a Pad for which `getPriceForPart` finds no article (for example
 * slotted foam other than 15 mm), so the parts list can still list it: by
 * style and strength, with its volume in m³ like the volumetric foam articles.
 * Returns null for every other part and for a pad that has an article.
 */
export function getPadWithoutArticleInfo(
  part: PartLike,
): PadWithoutArticleInfo | null {
  const props = part.properties ?? part;
  if (props.type !== "Pad" || getPriceForPart(part)) return null;
  const amount = getPadVolumeM3(props);
  if (amount === undefined || typeof props.strength !== "number") return null;
  const styleLabel = props.style ? PAD_STYLE_LABELS[props.style] : undefined;
  const style = styleLabel ? ` ${styleLabel}` : "";
  const strength = props.strength.toLocaleString("de-DE");
  return {
    description: `Schaumstoff${style} ${strength} mm`,
    amount,
    unit: PriceUnit.CubicMeter,
  };
}

function pricePad(props: PartLike): MaterialInfo | null {
  const size = getSizeXY(props.size);
  if (!size) return null;

  if (props.partSelection?.name) {
    return {
      amount: size.x * size.y * 1e-6,
      code: props.partSelection.name,
      description: props.partSelection.description ?? "",
      type: MaterialType.Other,
      unit: PriceUnit.SquareMeter,
    };
  }

  const volume = getPadVolumeM3(props);
  if (volume === undefined) return null;
  const code = getPadCode(props.style);
  if (!code) return null;

  return {
    amount: volume,
    code,
    description: "Schaumstoff",
    type: MaterialType.Other,
    unit: PriceUnit.CubicMeter,
  };
}

function priceRollerBoardPanel(props: PartLike): MaterialInfo | null {
  if (props.n !== 0) return null;
  const code = getPanelMaterialCode(props);
  if (!code) return null;
  return {
    amount: 1,
    code,
    description: rollbrettBoardDescription(props, props.braked === true),
    type: MaterialType.Panel,
    unit: PriceUnit.Piece,
  };
}

function priceSlideRailPanel(props: PartLike): MaterialInfo | null {
  const code = getPanelMaterialCode(props);
  if (!code) return null;
  return {
    amount: 1,
    code,
    description: props.material?.description ?? "",
    type: MaterialType.Panel,
    unit: PriceUnit.Piece,
  };
}

function priceSizedPanel(props: PartLike): MaterialInfo | null {
  const size = getSizeXY(props.size);
  if (!size) return null;
  const code = getPanelMaterialCode(props);
  if (!code) return null;
  return {
    amount: size.x * size.y * 1e-6,
    code,
    description: "Panel",
    type: MaterialType.Panel,
    unit: PriceUnit.SquareMeter,
  };
}

function pricePanel(props: PartLike): MaterialInfo | null {
  const panelType = normalizePanelType(props.panelType);
  if (panelType === "RollerBoard") {
    return priceRollerBoardPanel(props);
  }
  if (panelType === "SlideRail") {
    return priceSlideRailPanel(props);
  }
  return priceSizedPanel(props);
}

function priceTrayPanel(props: PartLike): MaterialInfo | null {
  const size = getSizeXY(props.size);
  if (!size) return null;
  const strength = props.strength;
  if (strength == null) return null;
  const code = resolveTrayPanelMaterialCode(props, strength);
  if (!code) return null;
  return {
    amount: size.x * size.y * 1e-6,
    code,
    description: props.color?.description ?? "Panel",
    type: MaterialType.Panel,
    unit: PriceUnit.SquareMeter,
  };
}

function priceSlideRail(props: PartLike): MaterialInfo | null {
  const strength = props.partSelection?.strength ?? props.strength;
  if (typeof strength !== "number") return null;
  return {
    amount: 1,
    code: strength === 18 ? "1004" : "1005",
    description: "Panel",
    type: MaterialType.Panel,
    unit: PriceUnit.Piece,
  };
}

function getPianoHingeSegmentCount(
  props: PartLike & {
    segments?: number;
    segmentLength?: number;
  },
): number | undefined {
  if (typeof props.segments === "number") {
    return props.segments;
  }
  if (typeof props.segmentLength === "number") {
    return getPianoHingeSegments(props.segmentLength);
  }
  return undefined;
}

function getPianoHingeSegmentWidthMm(
  partSelection: { segments?: number; width?: number } | undefined,
): number {
  const segmentsPerMesh = partSelection?.segments;
  const meshWidthMm = partSelection?.width;
  if (
    typeof meshWidthMm === "number" &&
    typeof segmentsPerMesh === "number" &&
    segmentsPerMesh > 0
  ) {
    return meshWidthMm / segmentsPerMesh;
  }
  return 25.4;
}

function pricePianoHinge(props: PartLike): MaterialInfo | null {
  const pianoProps = props as PartLike & {
    segments?: number;
    segmentLength?: number;
    partSelection?: { segments?: number; width?: number };
  };
  const segmentCount = getPianoHingeSegmentCount(pianoProps);
  if (segmentCount == null) return null;
  const segmentWidthMm = getPianoHingeSegmentWidthMm(pianoProps.partSelection);
  const totalLengthMm = segmentCount * segmentWidthMm;
  return {
    amount: totalLengthMm * 1e-3,
    code: "SHA2606",
    description: "Klavierband",
    type: MaterialType.PianoHinge,
    unit: PriceUnit.Meter,
  };
}

function priceProfile(props: PartLike): MaterialInfo | null {
  if (!props.partSelection?.name) return null;
  const points = props.points;
  if (!points || !Array.isArray(points)) return null;
  const totalLength = getProfileSegmentLengths(points).reduce(
    (sum, len) => sum + len,
    0,
  );
  return {
    amount: totalLength * 1e-3,
    code: props.partSelection.name.replace("i", ""),
    description: props.partSelection.description ?? "",
    type: MaterialType.Alu,
    unit: PriceUnit.Meter,
  };
}

function priceRoller(props: PartLike): MaterialInfo | null {
  const partSelection = props.partSelection;
  if (!partSelection?.name) return null;
  if (
    isRollbrettLabel(partSelection.name) ||
    isCreeperBundlePartSelection(partSelection)
  ) {
    return null;
  }
  // Roller names carry an installation location ("ROL37451S vorne rechts");
  // only the leading token is the article number.
  return pricePartSelectionPiece(props, articleCodeFromName);
}

export function getPriceForPart(part: PartLike): MaterialInfo | null {
  if (isArticleFreePart(part)) return null;
  const props = part.properties ?? part;

  switch (props.type) {
    case "AngleSection":
      return priceAngleSection(props);
    case "Pad":
      return pricePad(props);
    case "Panel":
      return pricePanel(props);
    case "TrayPanel":
      return priceTrayPanel(props);
    case "SlideRail":
      return priceSlideRail(props);
    case "PianoHinge":
      return pricePianoHinge(props);
    case "Hinge":
      // Hinge options carry a direction suffix ("... links") that is part of
      // neither the article number nor an installation location.
      return pricePartSelectionPiece(props, stripDirectionSuffix);
    case "Profile":
      return priceProfile(props);
    case "Roller":
      return priceRoller(props);
    default:
      // Article numbers never contain spaces; an option may append an
      // installation location ("ROL37451S hinten") — match on the leading
      // token so every location variant resolves to the same article.
      return pricePartSelectionPiece(props, articleCodeFromName);
  }
}
